// ============================================================================
// TOGGLE BLOCK — read-only mirror of
// siena-web/apps/web/components/docs/knowledge/toggle-block.ts (+ its node
// view), kept duplicated because the docs-site is a standalone Vite bundle and
// the editor package isn't published. If you change either copy, update both.
//
// This copy drops the editing surface — commands, keyboard shortcuts and the
// markdown serializer — and keeps only what a published page needs: the schema
// so `<details>` in the stored markdown parses into real nodes, the DOM
// normalizer, and a node view whose triangle readers can click.
// ============================================================================

import { Node, mergeAttributes } from "@tiptap/core";

export const TOGGLE_CONTENT_ATTR = "data-toggle-content";

const TOGGLE_BLOCK_NAME = "toggleBlock";

const TOGGLE_PRIORITY = 200;

const isHeadingLevel = (level: unknown): level is 1 | 2 | 3 =>
  level === 1 || level === 2 || level === 3;

/**
 * Ensure every `<details>` has exactly one `<summary>` followed by one wrapper
 * div, so it matches this schema. The editor writes that shape already; a
 * `<details>` hand-written by an author has its body as loose siblings of the
 * summary, which would otherwise not parse into `toggleContent`.
 */
const normalizeDetailsElements = (element: HTMLElement): void => {
  const doc = element.ownerDocument;
  if (!doc) return;

  element.querySelectorAll("details").forEach((details) => {
    const summary = details.querySelector(":scope > summary");

    if (!details.querySelector(`:scope > div[${TOGGLE_CONTENT_ATTR}]`)) {
      const wrapper = doc.createElement("div");
      wrapper.setAttribute(TOGGLE_CONTENT_ATTR, "");
      Array.from(details.childNodes).forEach((child) => {
        if (child !== summary) wrapper.appendChild(child);
      });
      details.appendChild(wrapper);
    }

    if (!summary) {
      details.insertBefore(doc.createElement("summary"), details.firstChild);
    }
  });
};

// ----------------------------------------------------------------------------
// Node view — the disclosure triangle readers click
// ----------------------------------------------------------------------------

const SVG_NS = "http://www.w3.org/2000/svg";

/** Notion's disclosure triangle, matching the editor's mark exactly. */
const TRIANGLE_PATH = "M4 2.6v6.8L9.1 6z";

const createTriangle = (doc: Document): SVGSVGElement => {
  const svg = doc.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "doc-toggle__triangle");
  svg.setAttribute("viewBox", "0 0 12 12");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  const path = doc.createElementNS(SVG_NS, "path");
  path.setAttribute("d", TRIANGLE_PATH);
  path.setAttribute("fill", "currentColor");
  svg.appendChild(path);

  return svg;
};

/**
 * A PLAIN ProseMirror node view, NOT `ReactNodeViewRenderer` — which returns an
 * empty spec until `editor.contentComponent` is mounted and silently falls back
 * to `renderHTML`. Every toggle on a published page comes in with the editor's
 * initial content, so it hits exactly that window: the page then renders a real
 * `<details>`, which has no triangle of ours to click.
 *
 * The state is local to the view. Nothing here is editable, so expanding a
 * section is a reading gesture and must never touch the document.
 */
const createToggleNodeView = (node: import("@tiptap/pm/model").Node) => {
  const dom = document.createElement("div");
  dom.className = "doc-toggle";

  const marker = document.createElement("button");
  marker.type = "button";
  marker.className = "doc-toggle__marker";
  marker.contentEditable = "false";
  marker.appendChild(createTriangle(document));

  const contentDOM = document.createElement("div");
  contentDOM.className = "doc-toggle__body";

  dom.appendChild(marker);
  dom.appendChild(contentDOM);

  let isOpen = node.attrs.open !== false;

  const paint = (current: import("@tiptap/pm/model").Node) => {
    dom.dataset.open = isOpen ? "true" : "false";
    dom.dataset.level = String(Number(current.firstChild?.attrs.level) || 0);
    marker.setAttribute("aria-expanded", isOpen ? "true" : "false");
    marker.setAttribute(
      "aria-label",
      isOpen ? "Collapse section" : "Expand section"
    );
  };

  paint(node);

  marker.addEventListener("click", (event) => {
    event.preventDefault();
    isOpen = !isOpen;
    paint(node);
  });

  return {
    dom,
    contentDOM,
    update: (updated: import("@tiptap/pm/model").Node) => {
      if (updated.type.name !== TOGGLE_BLOCK_NAME) return false;
      paint(updated);
      return true;
    },
    stopEvent: (event: Event) => marker.contains(event.target as globalThis.Node),
    ignoreMutation: (mutation: MutationRecord) =>
      !contentDOM.contains(mutation.target),
  };
};

// ----------------------------------------------------------------------------
// Nodes
// ----------------------------------------------------------------------------

export const ToggleBlock = Node.create({
  name: TOGGLE_BLOCK_NAME,
  priority: TOGGLE_PRIORITY,
  group: "block",
  content: "toggleSummary toggleContent",
  defining: true,

  addAttributes() {
    return {
      open: {
        default: true,
        parseHTML: (element) => element.hasAttribute("open"),
        renderHTML: (attributes) => (attributes.open === false ? {} : { open: "" }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "details" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["details", mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ({ node }) => createToggleNodeView(node);
  },

  addStorage() {
    return {
      markdown: {
        parse: {
          updateDOM: normalizeDetailsElements,
        },
      },
    };
  },
});

export const ToggleSummary = Node.create({
  name: "toggleSummary",
  priority: TOGGLE_PRIORITY,
  content: "inline*",
  defining: true,
  selectable: false,

  addAttributes() {
    return {
      level: {
        default: 0,
        // The heading element inside the <summary> IS the level, so there is no
        // separate attribute to drift out of sync.
        parseHTML: (element) => {
          const heading = element.querySelector("h1, h2, h3");
          return heading ? Number(heading.tagName.slice(1)) : 0;
        },
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "summary",
        contentElement: (element) =>
          (element as HTMLElement).querySelector("h1, h2, h3") ??
          (element as HTMLElement),
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attributes = mergeAttributes(HTMLAttributes);
    const level = node.attrs.level;
    if (isHeadingLevel(level)) {
      return ["summary", attributes, [`h${level}`, {}, 0]];
    }
    return ["summary", attributes, 0];
  },
});

export const ToggleContent = Node.create({
  name: "toggleContent",
  priority: TOGGLE_PRIORITY,
  content: "block+",
  defining: true,
  selectable: false,

  parseHTML() {
    return [{ tag: `div[${TOGGLE_CONTENT_ATTR}]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { [TOGGLE_CONTENT_ATTR]: "" }), 0];
  },
});

export const ToggleBlockExtensions = [ToggleBlock, ToggleSummary, ToggleContent];
